<template>
  <div class="admin-management">
    <div class="page-header">
      <div class="header-content">
        <h1>{{ $t('admin.title') }}</h1>
        <p class="header-subtitle">{{ $t('admin.subtitle') }}</p>
      </div>
      <button @click="showAddForm = true" class="btn btn-primary">
        {{ $t('admin.addUser') }}
      </button>
    </div>

    <!-- Форма добавления пользователя -->
    <transition name="slide-fade">
      <div v-if="showAddForm" class="add-form-compact">
        <form @submit.prevent="addAdmin" class="form-content-compact">
          <div class="form-row">
            <input 
              v-model="newAdminEmail" 
              type="email" 
              required 
              placeholder="Email: user@example.com"
              class="form-input-compact"
            />
            <select v-model="newAdminRole" required class="form-select-compact">
              <option value="user">User</option>
              <option value="administrator">Administrator</option>
              <option value="owner" v-if="currentUserRole === 'owner'">Owner</option>
            </select>
            <button type="submit" class="btn btn-primary btn-compact" :disabled="loading">
              {{ loading ? '...' : t('common.add') }}
            </button>
            <button type="button" @click="cancelAdd" class="btn-close-compact" :title="t('common.cancel')">×</button>
          </div>
        </form>
      </div>
    </transition>

    <!-- Список пользователей -->
    <div class="users-section">
      <div v-if="loadingAdmins" class="loading-state">
        <div class="spinner"></div>
        {{ $t('admin.loading') }}
      </div>
      
      <div v-else-if="admins.length === 0" class="empty-state">
        {{ $t('admin.noUsers') }}
      </div>
      
      <div v-else class="users-list">
        <div 
          v-for="admin in admins" 
          :key="admin.email"
          :class="['user-row', { 'current-user': admin.is_current }]"
        >
          <div class="user-info-section">
            <div class="user-avatar">
              <img 
                v-if="admin.picture" 
                :src="admin.picture" 
                :alt="admin.email"
                class="avatar-image"
                @error="handleImageError"
              />
              <span v-else class="avatar-initials">
                {{ admin.email.charAt(0).toUpperCase() }}
              </span>
            </div>
            <div class="user-details">
              <div class="user-email">{{ admin.email }}</div>
            </div>
          </div>
          
          <div class="user-actions-section">
            <!-- Dropdown для роли и beta -->
            <div v-if="!admin.is_current && canManageUser(admin.role)" class="role-dropdown">
              <button 
                @click="toggleDropdown(admin.email)"
                class="role-dropdown-btn"
                :disabled="loading"
              >
                <span :class="['role-text', admin.role]">{{ getRoleDisplayName(admin.role) }}</span>
                <span v-if="admin.is_beta_tester" class="beta-indicator">🧪</span>
                <svg class="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                  <path d="M6 8L2 4h8z"/>
                </svg>
              </button>
              <transition name="dropdown-fade">
                <div 
                  v-if="openDropdownEmail === admin.email" 
                  class="role-dropdown-menu" 
                  @click.stop
                >
                  <div class="dropdown-section">
                    <div class="dropdown-label">{{ t('admin.role') }}</div>
                    <button 
                      v-for="role in getAvailableRoles()" 
                      :key="role.value"
                      @click="setRole(admin.email, role.value)"
                      :class="['dropdown-item', { active: admin.role === role.value }]"
                    >
                      {{ role.label }}
                    </button>
                  </div>
                  <div class="dropdown-divider"></div>
                  <div class="dropdown-section">
                    <button 
                      @click="setBeta(admin.email, !admin.is_beta_tester)"
                      :class="['dropdown-item', 'beta-item', { active: admin.is_beta_tester }]"
                    >
                      <span class="beta-icon">🧪</span>
                      {{ t('admin.betaTester') }}
                      <span v-if="admin.is_beta_tester" class="checkmark">✓</span>
                    </button>
                  </div>
                  <!-- 🆕 Quality Mode (только для owner) -->
                  <template v-if="currentUserRole === 'owner'">
                    <div class="dropdown-divider"></div>
                    <div class="dropdown-section">
                      <div class="dropdown-label">{{ t('admin.qualitySettings') }}</div>
                      <button 
                        @click="toggleQualityMode(admin.email, admin.user_id, !admin.use_quality_mode)"
                        :class="['dropdown-item', 'quality-item', { active: admin.use_quality_mode }]"
                      >
                        <span class="quality-icon">💎</span>
                        {{ t('admin.qualityMode') }}
                        <span v-if="admin.use_quality_mode" class="checkmark">✓</span>
                      </button>
                    </div>
                    <!-- Storage Limit -->
                    <div class="dropdown-divider"></div>
                    <div class="dropdown-section">
                      <div class="dropdown-label">{{ t('admin.storageLimit') }}</div>
                      <div class="storage-limit-control">
                        <div class="storage-usage-mini">
                          <span class="storage-used-text">{{ admin.storage_used_mb?.toFixed(1) || '0' }} MB</span>
                          <span class="storage-separator">/</span>
                          <input 
                            type="number" 
                            v-model.number="admin.storage_limit_input"
                            @change="updateStorageLimit(admin.email, admin.user_id, admin.storage_limit_input)"
                            @click.stop
                            min="0"
                            max="10000"
                            class="storage-limit-input"
                          />
                          <span class="storage-unit">MB</span>
                        </div>
                        <div class="storage-bar-mini">
                          <div 
                            class="storage-bar-fill-mini" 
                            :style="{ width: Math.min((admin.storage_used_mb || 0) / (admin.storage_limit_mb || 500) * 100, 100) + '%' }"
                            :class="{ warning: (admin.storage_used_mb || 0) / (admin.storage_limit_mb || 500) > 0.8 }"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </transition>
            </div>
            <!-- Readonly для текущего пользователя -->
            <div v-else class="role-badge-readonly">
              <span :class="['role-text', admin.role]">{{ getRoleDisplayName(admin.role) }}</span>
              <span v-if="admin.is_beta_tester" class="beta-indicator">🧪</span>
            </div>
            
            <button 
              v-if="!admin.is_current && canManageUser(admin.role)"
              @click="confirmDelete(admin.email)"
              class="btn-remove"
              :disabled="loading"
              :title="t('admin.deleteUser')"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 0 0 .249.225h5.19a.25.25 0 0 0 .249-.225l.66-6.6a.75.75 0 0 1 1.492.149l-.66 6.6A1.748 1.748 0 0 1 10.595 15h-5.19a1.748 1.748 0 0 1-1.741-1.575l-.66-6.6a.75.75 0 1 1 1.492-.15zM6.5 1.75V3h3V1.75a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Уведомления -->
    <transition name="fade">
      <div v-if="message" :class="['notification', messageType]">
        {{ message }}
      </div>
    </transition>

    <!-- Диалог подтверждения удаления -->
    <transition name="modal-fade">
      <div v-if="deleteConfirmEmail" class="modal-overlay" @click="cancelDelete">
        <div class="delete-confirm-dialog" @click.stop>
          <div class="delete-confirm-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <h3>{{ t('admin.deleteUserTitle') }}</h3>
          </div>
          <p class="delete-confirm-message">
            <strong>{{ deleteConfirmEmail }}</strong>
          </p>
          <div class="delete-confirm-actions">
            <button @click="cancelDelete" class="btn btn-secondary">Отмена</button>
            <button @click="removeAdmin" class="btn btn-danger" :disabled="loading">
              {{ loading ? 'Удаление...' : 'Удалить' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { authService } from '../services/auth'
import { useLanguage } from '../composables/useLanguage'

export default {
  name: 'AdminManagement',
  setup() {
    const { t } = useLanguage()
    const admins = ref([])
    const loadingAdmins = ref(false)
    const loading = ref(false)
    const showAddForm = ref(false)
    const newAdminEmail = ref('')
    const newAdminRole = ref('user')
    const currentUserRole = ref('')
    const message = ref('')
    const messageType = ref('')
    const openDropdownEmail = ref(null)
    const deleteConfirmEmail = ref(null)

    const apiRequest = async (url, options = {}) => {
      const token = await authService.getToken()  // ИСПРАВЛЕНО: добавлен await для async функции
      const headers = {
        'Content-Type': 'application/json',
        ...options.headers
      }
      
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await fetch(url, {
        ...options,
        headers
      })

      if (response.status === 401) {
        authService.logout()
        window.location.reload()
        return null
      }

      return response
    }

    const showMessage = (text, type = 'info') => {
      message.value = text
      messageType.value = type
      setTimeout(() => {
        message.value = ''
        messageType.value = ''
      }, 5000)
    }

    const sortAdmins = (adminsList) => {
      // Иерархия ролей для сортировки
      const roleOrder = {
        'owner': 3,
        'administrator': 2,
        'user': 1
      }
      
      return [...adminsList].sort((a, b) => {
        // Текущий пользователь всегда первый
        if (a.is_current) return -1
        if (b.is_current) return 1
        
        // Затем по ролям (от высшей к низшей)
        const roleA = roleOrder[a.role] || 0
        const roleB = roleOrder[b.role] || 0
        
        return roleB - roleA
      })
    }

    const loadAdmins = async () => {
      loadingAdmins.value = true
      try {
        const response = await apiRequest('/admin/admins')
        if (response && response.ok) {
          const data = await response.json()
          
          // 🆕 Загружаем настройки для каждого пользователя (только для owner)
          const adminsWithSettings = await Promise.all(
            data.admins.map(async (admin) => {
              // Добавляем user_id (берём из email до @)
              const user_id = admin.email.split('@')[0]
              admin.user_id = user_id
              
              // Загружаем настройки только для owner
              if (currentUserRole.value === 'owner' || data.admins.find(a => a.is_current)?.role === 'owner') {
                try {
                  // Load user settings
                  const settingsResponse = await apiRequest(`/admin/users/${user_id}/settings`)
                  if (settingsResponse && settingsResponse.ok) {
                    const settingsData = await settingsResponse.json()
                    admin.use_quality_mode = settingsData.settings?.use_quality_mode || false
                  } else {
                    admin.use_quality_mode = false
                  }
                  
                  // Load storage info
                  const storageResponse = await apiRequest(`/admin/users/${user_id}/storage`)
                  if (storageResponse && storageResponse.ok) {
                    const storageData = await storageResponse.json()
                    admin.storage_used_mb = storageData.used_mb || 0
                    admin.storage_limit_mb = storageData.limit_mb || 500
                    admin.storage_limit_input = storageData.limit_mb || 500
                  } else {
                    admin.storage_used_mb = 0
                    admin.storage_limit_mb = 500
                    admin.storage_limit_input = 500
                  }
                } catch (err) {
                  console.warn(`Failed to load settings for ${admin.email}:`, err)
                  admin.use_quality_mode = false
                  admin.storage_used_mb = 0
                  admin.storage_limit_mb = 500
                  admin.storage_limit_input = 500
                }
              } else {
                admin.use_quality_mode = false
                admin.storage_used_mb = 0
                admin.storage_limit_mb = 500
                admin.storage_limit_input = 500
              }
              
              return admin
            })
          )
          
          admins.value = sortAdmins(adminsWithSettings)

          const currentUser = admins.value.find(admin => admin.is_current)
          if (currentUser) {
            currentUserRole.value = currentUser.role
          }
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('admin.loadError'), 'error')
        }
      } catch (error) {
        console.error('Error loading admins:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loadingAdmins.value = false
      }
    }

    const addAdmin = async () => {
      if (!newAdminEmail.value.trim() || !newAdminRole.value.trim()) return

      loading.value = true
      try {
        const response = await apiRequest('/admin/admins', {
          method: 'POST',
          body: JSON.stringify({
            email: newAdminEmail.value.trim(),
            role: newAdminRole.value.trim()
          })
        })

        if (response && response.ok) {
          showMessage(t('admin.userAdded'), 'success')
          newAdminEmail.value = ''
          newAdminRole.value = 'user'
          showAddForm.value = false
          await loadAdmins()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('admin.addError'), 'error')
        }
      } catch (error) {
        console.error('Error adding admin:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const confirmDelete = (email) => {
      deleteConfirmEmail.value = email
    }

    const cancelDelete = () => {
      deleteConfirmEmail.value = null
    }

    const removeAdmin = async () => {
      const email = deleteConfirmEmail.value
      if (!email) return

      loading.value = true
      try {
        const response = await apiRequest(`/admin/admins/${encodeURIComponent(email)}`, {
          method: 'DELETE'
        })

        if (response && response.ok) {
          showMessage(t('admin.userDeleted'), 'success')
          await loadAdmins()
          deleteConfirmEmail.value = null
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || 'Ошибка удаления пользователя', 'error')
        }
      } catch (error) {
        console.error('Error removing admin:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const getRoleDisplayName = (role) => {
      const roleNames = {
        'owner': 'owner',
        'administrator': 'administrator', 
        'user': 'user'
      }
      return roleNames[role] || role
    }

    const canManageUser = (userRole) => {
      const roleHierarchy = {
        'owner': 3,
        'administrator': 2,
        'user': 1
      }
      
      const currentLevel = roleHierarchy[currentUserRole.value] || 0
      const targetLevel = roleHierarchy[userRole] || 0
      
      if (currentUserRole.value === 'owner') {
        return true
      }
      
      return currentLevel > targetLevel
    }

    const updateUserRole = async (email, newRole) => {
      if (!email || !newRole) return

      loading.value = true
      try {
        const response = await apiRequest(`/admin/admins/${encodeURIComponent(email)}`, {
          method: 'PATCH',
          body: JSON.stringify({
            role: newRole
          })
        })

        if (response && response.ok) {
          showMessage(t('admin.roleChanged'), 'success')
          await loadAdmins()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('admin.roleChangeError'), 'error')
        }
      } catch (error) {
        console.error('Error updating user role:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const toggleBetaTester = async (email, isBetaTester) => {
      if (!email) return

      loading.value = true
      try {
        const response = await apiRequest(`/admin/admins/${encodeURIComponent(email)}`, {
          method: 'PATCH',
          body: JSON.stringify({
            is_beta_tester: isBetaTester
          })
        })

        if (response && response.ok) {
          showMessage(isBetaTester ? t('admin.betaTesterAdded') : t('admin.betaTesterRemoved'), 'success')
          await loadAdmins()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('admin.betaTesterError'), 'error')
        }
      } catch (error) {
        console.error('Error toggling beta tester:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const getAvailableRoles = () => {
      const roles = [
        { value: 'user', label: 'user' },
        { value: 'administrator', label: 'administrator' }
      ]
      
      if (currentUserRole.value === 'owner') {
        roles.push({ value: 'owner', label: 'owner' })
      }
      
      return roles
    }

    const cancelAdd = () => {
      showAddForm.value = false
      newAdminEmail.value = ''
      newAdminRole.value = 'user'
    }

    const handleImageError = (event) => {
      // Если аватар не загрузился, скрываем img и показываем инициалы
      event.target.style.display = 'none'
      const parent = event.target.parentElement
      const initials = parent.querySelector('.avatar-initials')
      if (initials) {
        initials.style.display = 'flex'
      }
    }

    const toggleDropdown = (email) => {
      openDropdownEmail.value = openDropdownEmail.value === email ? null : email
    }

    const closeDropdown = () => {
      openDropdownEmail.value = null
    }

    const updateRoleAndBeta = async (email, updates) => {
      if (!email) return

      loading.value = true
      try {
        const response = await apiRequest(`/admin/admins/${encodeURIComponent(email)}`, {
          method: 'PATCH',
          body: JSON.stringify(updates)
        })

        if (response && response.ok) {
          showMessage(t('admin.changesSaved'), 'success')
          await loadAdmins()
          closeDropdown()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('admin.changeError'), 'error')
        }
      } catch (error) {
        console.error('Error updating user:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const setRole = async (email, role) => {
      await updateRoleAndBeta(email, { role })
    }

    const setBeta = async (email, isBeta) => {
      await updateRoleAndBeta(email, { is_beta_tester: isBeta })
    }

    // 🆕 Управление Quality Mode для пользователя (только owner)
    const toggleQualityMode = async (email, userId, enableQuality) => {
      if (currentUserRole.value !== 'owner') {
        showMessage('Только owner может изменять настройки обработки', 'error')
        return
      }

      loading.value = true
      try {
        const response = await apiRequest(`/admin/users/${userId}/settings`, {
          method: 'PUT',
          body: JSON.stringify({
            use_quality_mode: enableQuality
          })
        })

        if (response && response.ok) {
          const modeText = enableQuality ? 'включён' : 'выключен'
          showMessage(`Режим качественной обработки ${modeText} для ${email}`, 'success')
          await loadAdmins()
          closeDropdown()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || 'Ошибка изменения настроек', 'error')
        }
      } catch (error) {
        console.error('Error updating quality mode:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    // 🆕 Управление лимитом хранилища (только owner)
    const updateStorageLimit = async (email, userId, limitMb) => {
      if (currentUserRole.value !== 'owner') {
        showMessage('Только owner может изменять лимит хранилища', 'error')
        return
      }

      if (!limitMb || limitMb < 0) {
        showMessage('Некорректное значение лимита', 'error')
        return
      }

      loading.value = true
      try {
        const response = await apiRequest(`/admin/users/${userId}/storage/limit`, {
          method: 'PUT',
          body: JSON.stringify({
            limit_mb: limitMb
          })
        })

        if (response && response.ok) {
          showMessage(`Лимит хранилища для ${email}: ${limitMb} MB`, 'success')
          // Update local state
          const admin = admins.value.find(a => a.email === email)
          if (admin) {
            admin.storage_limit_mb = limitMb
          }
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || 'Ошибка изменения лимита', 'error')
        }
      } catch (error) {
        console.error('Error updating storage limit:', error)
        showMessage('Ошибка подключения к серверу', 'error')
      } finally {
        loading.value = false
      }
    }

    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('.role-dropdown')
      let clickedInside = false
      
      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target)) {
          clickedInside = true
        }
      })
      
      if (!clickedInside) {
        closeDropdown()
      }
    }

    onMounted(() => {
      loadAdmins()
      document.addEventListener('click', handleClickOutside)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      t,
      admins,
      loadingAdmins,
      loading,
      showAddForm,
      newAdminEmail,
      newAdminRole,
      currentUserRole,
      handleImageError,
      message,
      messageType,
      openDropdownEmail,
      deleteConfirmEmail,
      addAdmin,
      removeAdmin,
      confirmDelete,
      cancelDelete,
      updateUserRole,
      toggleBetaTester,
      cancelAdd,
      getRoleDisplayName,
      canManageUser,
      getAvailableRoles,
      toggleDropdown,
      closeDropdown,
      setRole,
      setBeta,
      toggleQualityMode,
      updateStorageLimit
    }
  }
}
</script>

<style scoped>
/* ============================================
   ADMIN MANAGEMENT COMPONENT STYLES
   
   ВАЖНО: Переиспользуемые стили вынесены в:
   - styles/components.css - кнопки, badges, spinner, loading/empty states
   - styles/admin.css - таблицы, статистика, фильтры, user-row
   - styles/responsive.css - адаптивность
   
   Здесь только уникальные стили для компонента управления пользователями
   ============================================ */

.admin-management {
  /* Убираем padding - он уже есть в container */
  max-width: 1200px;
  margin: 0 auto;
}

/* Заголовок - выравниваем с остальными секциями */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.header-content h1 {
  margin: 0 0 1.5rem 0;
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.header-subtitle {
  margin: 0.5rem 0 0 0;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

/* Кнопки */
.btn {
  padding: 0.375rem 1rem;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.btn:hover:not(:disabled) {
  background: var(--card-hover-bg);
  border-color: var(--border-color);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--success-color);
  color: white;
  border-color: var(--success-color);
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.9;
}

.btn-secondary {
  background: var(--bg-secondary);
  border-color: var(--border-light);
  color: var(--text-primary);
}

.btn-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.btn-close:hover {
  background: var(--card-hover-bg);
  color: var(--text-primary);
}

/* Форма добавления - Компактная версия */
.add-form-compact {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-light);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
}

.form-content-compact {
  display: flex;
  width: 100%;
}

.form-row {
  display: flex;
  gap: 0.5rem;
  width: 100%;
  align-items: center;
}

.form-input-compact {
  flex: 1;
  min-width: 200px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.form-select-compact {
  width: 140px;
  padding: 0.5rem 0.5rem;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
}

.form-input-compact:focus,
.form-select-compact:focus {
  outline: none;
  border-color: var(--info-color);
  box-shadow: 0 0 0 2px var(--info-bg);
}

.btn-compact {
  padding: 0.5rem 1rem;
  white-space: nowrap;
}

.btn-close-compact {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

.btn-close-compact:hover {
  background: var(--card-hover-bg);
  color: var(--text-primary);
}

/* Список пользователей */
.users-section {
  min-height: 200px;
}

/* .loading-state, .spinner, .empty-state - используются из components.css */

.users-list {
  border: 1px solid var(--border-light);
  border-radius: 6px;
  overflow: visible;
  background: var(--bg-secondary);
}

.user-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
  transition: background-color 0.15s ease;
  gap: 1.5rem;
}

.user-row:last-child {
  border-bottom: none;
}

.user-row:hover {
  background: var(--card-hover-bg);
}

.user-row.current-user {
  background: var(--info-bg);
}

.user-row.current-user:hover {
  background: var(--info-bg);
  opacity: 0.9;
}

.user-info-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 200px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.user-details {
  display: flex;
  align-items: start;
  flex: 1;
  min-width: 0;
}

.user-email {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.user-label {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: var(--info-bg);
  border: 1px solid var(--info-color);
  color: var(--info-color);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
}

.user-actions-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Role Dropdown */
.role-dropdown {
  position: relative;
  min-width: 140px;
  flex-shrink: 0;
}

.role-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-light);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s ease;
  width: 100%;
}

.role-dropdown-btn:hover:not(:disabled) {
  background: var(--card-hover-bg);
  border-color: var(--border-color);
}

.role-dropdown-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.role-dropdown-btn .dropdown-arrow {
  margin-left: auto;
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.role-dropdown-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;  /* 🔧 Увеличено с 1000 до 9999 */
  min-width: 180px;
  padding: 0.5rem 0;
  animation: dropdownFadeIn 0.2s ease;
}

.dropdown-section {
  padding: 0.25rem 0;
}

.dropdown-label {
  padding: 0.25rem 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dropdown-item {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  text-align: left;
  font-size: 0.75rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-item:hover {
  background: var(--card-hover-bg);
}

.dropdown-item.active {
  background: var(--info-bg);
  color: var(--info-color);
  font-weight: 600;
}

.dropdown-item.beta-item {
  color: var(--info-color);
}

.dropdown-item.beta-item.active {
  background: var(--info-bg);
}

.beta-icon {
  font-size: 0.875rem;
}

/* Storage Limit Control */
.storage-limit-control {
  padding: 0.5rem 0.75rem;
}

.storage-usage-mini {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}

.storage-used-text {
  color: var(--text-primary);
  font-weight: 500;
}

.storage-separator {
  color: var(--text-secondary);
}

.storage-limit-input {
  width: 60px;
  padding: 0.25rem 0.375rem;
  border: 1px solid var(--border-light);
  border-radius: 4px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 0.75rem;
  text-align: right;
}

.storage-limit-input:focus {
  outline: none;
  border-color: var(--info-color);
}

.storage-unit {
  color: var(--text-secondary);
  font-size: 0.75rem;
}

.storage-bar-mini {
  height: 4px;
  background: var(--border-light);
  border-radius: 2px;
  overflow: hidden;
}

.storage-bar-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #4ade80);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.storage-bar-fill-mini.warning {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.checkmark {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--success-color);
}

.dropdown-divider {
  height: 1px;
  background: var(--border-light);
  margin: 0.25rem 0;
}

.beta-indicator {
  font-size: 0.75rem;
}

.role-badge-readonly {
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-light);
  border-radius: 6px;
}

.role-text {
  font-weight: 600;
}

.role-text.owner {
  color: var(--error-color);
}

.role-text.administrator {
  color: var(--warning-color);
}

.role-text.user {
  color: var(--success-color);
}

/* Анимация dropdown */
@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-fade-enter-active {
  animation: dropdownFadeIn 0.2s ease;
}

.dropdown-fade-leave-active {
  animation: dropdownFadeIn 0.15s ease reverse;
}

.btn-remove {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-remove:hover:not(:disabled) {
  background: var(--error-bg);
  color: var(--error-color);
}

.btn-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Уведомления */
.notification {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid;
  z-index: 1000;
  max-width: 400px;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.notification.success {
  background: var(--success-bg);
  color: var(--success-color);
  border-color: var(--success-color);
}

.notification.error {
  background: var(--error-bg);
  color: var(--error-color);
  border-color: var(--error-color);
}

.notification.info {
  background: var(--info-bg);
  color: var(--info-color);
  border-color: var(--info-color);
}

/* Анимации */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.15s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Адаптивность для компонента AdminManagement */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-primary {
    width: 100%;
    justify-content: center;
  }
  
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .form-input-compact,
  .form-select-compact {
    width: 100%;
    min-width: auto;
  }
  
  .btn-compact {
    width: 100%;
    justify-content: center;
  }
  
  .notification {
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
}

/* Остальные адаптивные стили (.user-row, .user-info-section, .user-actions-section, .role-selector) - см. responsive.css */

/* Диалог подтверждения удаления */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  overscroll-behavior: contain;
  touch-action: none;
}

.delete-confirm-dialog {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 2rem;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
  touch-action: auto;
}

.delete-confirm-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: var(--error-color);
}

.delete-confirm-header svg {
  flex-shrink: 0;
}

.delete-confirm-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.delete-confirm-message {
  margin: 0 0 1.5rem 0;
  color: var(--text-secondary);
  line-height: 1.8;
  text-align: center;
  font-size: 0.95rem;
  padding: 0 0.5rem;
}

.delete-confirm-message strong {
  color: var(--text-primary);
  font-weight: 600;
  display: block;
  margin-top: 0.75rem;
  font-size: 1rem;
}

.delete-confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.delete-confirm-actions .btn {
  min-width: 100px;
}

.btn-danger {
  background: var(--error-color);
  color: white;
}

.btn-danger:hover {
  background: #c62828;
  transform: translateY(-1px);
}

.btn-danger:active {
  transform: translateY(0);
}

.btn-danger:disabled {
  background: var(--error-bg);
  color: var(--error-color);
  cursor: not-allowed;
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .delete-confirm-dialog {
  animation: dialogSlideIn 0.3s ease;
}

@keyframes dialogSlideIn {
  from {
    transform: scale(0.9) translateY(-20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}
</style>
